// ---------------------------------------------------------------------------
// The browser side of NO-SHOW AND LATE MANAGEMENT. V28.
//
// NO STAGE ID, NO GROUND, NO OUTCOME AND NO TONE IS SPELLED HERE OR IN EITHER
// SCREEN. All four vocabularies arrive from `/v1/reference/reliability-vocabulary`
// with their labels, their tones and — for an outcome — `sets_aside`, which is the
// property that says whether an upheld appeal actually removed the shift.
//
// V27's GALLERY IS WHY THAT ENDPOINT EXISTS. A screen printed a raw vocabulary id
// into a sentence about money because a label fell back to the id and nothing
// served the labels. A vocabulary with no way to fetch its words is a vocabulary
// the browser eventually spells itself.
//
// AND NO TIME IS COMPUTED IN THE BROWSER. `call_at`, `evaluated_at` and
// `minutes_overdue` all arrive from the server, derived in SQL from the call time.
// A browser that worked out how late somebody was would be using the one clock
// the reader can change.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";

export type ReliabilityFactor = {
  factor: string;
  factor_label: string;
  weight: number | null;
  /**
   * FALSE means this term was left out of the numerator AND the denominator.
   * Not "scored zero" — a worker on their first shift has no attendance history,
   * and scoring that zero would make a first shift unreachable by the ranking
   * that decides who gets offered one.
   */
  is_measurable: boolean;
  value: number | null;
  contribution: number | null;
  sentence: string;
  /** 0267. True for the factor that counts turning up, false for being on time. */
  counts_arrivals: boolean;
};

export type Reliability = {
  score: number | null;
  shifts_counted: number;
  /** Shifts an upheld appeal removed. They count in neither direction. */
  shifts_set_aside: number;
  /** S74 (0218). The counts the factor sentences are built from; the screen words them. */
  shifts_arrived: number;
  shifts_on_time: number;
  factors: ReliabilityFactor[];
};

export type Escalation = {
  id: string;
  assignment_id: string;
  worker_name: string;
  stage: string;
  stage_label: string;
  tone: string;
  evaluated_at: string;
  call_at: string;
  minutes_overdue: number;
  /** NULL means nobody was standing by, which is a fact the screen states. */
  promoted_worker_name: string | null;
};

export type Appeal = {
  id: string;
  assignment_id: string;
  worker_id: string;
  worker_name: string;
  ground: string;
  ground_label: string;
  opened_at: string;
  outcome: string | null;
  outcome_label: string | null;
  outcome_tone: string | null;
  sets_aside: boolean | null;
  decided_at: string | null;
  /** THE NAMED HUMAN. A decision with no author is not a human-review route. */
  decided_by_name: string | null;
  /** S103. `/v1/my/reliability` only: the worker is told their agency decided, worded here. */
  decided_by_agency?: boolean;
};

export type AppealableShift = {
  assignment_id: string;
  stage_label: string;
  starts_at: string;
  event_name: string;
};

export type AppealGround = { id: string; label: string; rank: number; description: string };

export const reliability = {
  vocabulary: (base: string) => read(base, "/v1/reference/reliability-vocabulary"),

  eventEscalations: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/escalations`),

  // NO INSTANT IS SENT. The database reads its own clock; the replay parameter
  // exists for reproducing a past run and never reaches the network.
  escalate: (base: string) => mutate("POST", base, "/v1/attendance/escalate", {}),

  worker: (base: string, workerId: string) =>
    read(base, `/v1/workers/${encodeURIComponent(workerId)}/reliability`),

  mine: (base: string) => read(base, "/v1/my/reliability"),

  // A GROUND AND NOTHING ELSE. There is no note, no statement and no detail
  // field on this call at any layer — the honest sentence a person would type
  // here is health data and this record is readable by more than one role.
  appeal: (base: string, payload: { assignment_id: string; ground: string }) =>
    mutate("POST", base, "/v1/my/reliability/appeals", payload),

  appeals: (base: string) => read(base, "/v1/reliability/appeals"),

  decide: (base: string, id: string, outcome: string) =>
    mutate("POST", base, `/v1/reliability/appeals/${encodeURIComponent(id)}/decision`, { outcome }),
};
